// Copyright © 2026 Qcells. All rights reserved.
//
// This software is proprietary and confidential. Unauthorized use,
// duplication, or distribution of software is strictly prohibited.
//
// Alarm service. Query-shaped like the other services (see planService.ts) so
// screens and hooks stay unchanged if the backend seam moves again.
import type { Alarm, AlarmBucket, AlarmQuery } from '../types/alarm';
import type { AlarmApiRecord } from '../types/alarmApi';
import type { OperatorEvent } from '../types/alarmFsm';
import { applyAlarmEvent, listAllAlarms, putAlarmAssignment } from './alarmApiService';
import type { AlarmAssignmentAction } from './alarmApiService';
import { alarmApiRecordToAlarm, alarmRecordUid } from './alarmMappers';
import { MOCK_ALARM_RECORDS } from '../mocks/alarms';

/**
 * Drop duplicate rows, keyed the same way the card grid keys them.
 *
 * Nothing in an AEM row's response columns is guaranteed unique (`id` is
 * internal-only upstream), and draining several pages makes a repeat more likely
 * — a repeated React key breaks the grid's reconciliation.
 */
function dedupeByUid(records: AlarmApiRecord[]): AlarmApiRecord[] {
  const seen = new Set<string>();
  return records.filter(record => {
    const uid = alarmRecordUid(record);
    if (seen.has(uid)) return false;
    seen.add(uid);
    return true;
  });
}

export function shouldMockAlarms(): boolean {
  return process.env.REACT_APP_FORCE_ALARM_MOCKED_DATA === 'true';
}

/** Fetch + map one site's alarms. Empty when no site is given (real mode). */
async function fetchAlarms(siteId?: string): Promise<Alarm[]> {
  if (shouldMockAlarms()) {
    return MOCK_ALARM_RECORDS(siteId ?? 'mock-site').map(record => alarmApiRecordToAlarm(record));
  }
  if (!siteId) {
    // TODO(aem): the alarm-api is per-site only (no fleet endpoint). A
    // whole-fleet fetch needs the caller's full site list — see
    // alarmApiService.listAlarmsForSites — rather than being faked here.
    return [];
  }
  const records = await listAllAlarms(siteId);
  return dedupeByUid(records).map(record => alarmApiRecordToAlarm(record));
}

/** All alarms for a site (empty when `siteId` is omitted). */
export async function getAlarms(query: AlarmQuery = {}): Promise<Alarm[]> {
  return fetchAlarms(query.siteId);
}

/** Legacy per-bucket counts (drives the Site/Fleet ContextualSubnav). */
export async function getBucketCounts(siteId?: string): Promise<Record<AlarmBucket, number>> {
  const alarms = await fetchAlarms(siteId);
  const active = alarms.filter(a => (a.kind ?? 'alarm') === 'alarm');
  return {
    // TODO(aem): no severity source yet — every open alarm counts as critical
    // until real severity data lets alarms split across buckets.
    critical: active.filter(a => !a.closed).length,
    high: 0,
    // TODO(aem): AEM is alarms-only — routine/optwork/task come from other
    // upstreams that aren't wired up yet.
    events: 0,
    closed: active.filter(a => a.closed).length,
  };
}

/**
 * Send an operator event to an alarm's state machine.
 *
 * This replaces `patchConsoleAlarm`/`acknowledgeAlarm`/`closeAlarm`, which set
 * `alarm_status` directly through a PATCH that no longer exists upstream. An
 * operator no longer names a target state at all: they name an event, and the
 * machine decides the state. `assigned_to` had no replacement in the new
 * contract and is dropped with the patch — apply-event carries an event only.
 *
 * Callers must pass the row's `alarm_id`, not its code. In forced-mock mode
 * there is no real alarm to act on, so this is a no-op — the caller's own
 * refetch or optimistic update is all that happens locally.
 */
export async function sendAlarmEvent(
  siteId: string,
  alarmId: string,
  event: OperatorEvent
): Promise<void> {
  if (shouldMockAlarms()) return;
  await applyAlarmEvent(siteId, alarmId, event);
}

/**
 * Claim an alarm or release it. As with sendAlarmEvent, in mock mode there is no
 * real alarm to write to, so this is a no-op.
 */
export async function sendAlarmAssignment(
  siteId: string,
  alarmId: string,
  action: AlarmAssignmentAction
): Promise<void> {
  if (shouldMockAlarms()) return;
  await putAlarmAssignment(siteId, alarmId, action);
}
